import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageSessions = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'the-college-mentality',
    duration: 10,
    difficulty: 'beginner',
    image_url: '',
    audio_url: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      if (editingSession) {
        await axios.put(`${API}/admin/sessions/${editingSession.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/admin/sessions`, formData, { headers });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchSessions();
    } catch (error) {
      console.error('Failed to save session:', error);
      alert('Failed to save session');
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/admin/sessions/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSessions();
    } catch (error) {
      console.error('Failed to delete session:', error);
      alert('Failed to delete session');
    }
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    setFormData({
      title: session.title,
      description: session.description,
      category: session.category,
      duration: session.duration,
      difficulty: session.difficulty,
      image_url: session.image_url || '',
      audio_url: session.audio_url || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingSession(null);
    setFormData({
      title: '',
      description: '',
      category: 'the-college-mentality',
      duration: 10,
      difficulty: 'beginner',
      image_url: '',
      audio_url: ''
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API}/admin/upload/image`, uploadFormData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData({ ...formData, image_url: response.data.url });
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingAudio(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API}/admin/upload/audio`, uploadFormData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData({ ...formData, audio_url: response.data.url });
      alert('Audio uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload audio:', error);
      alert('Failed to upload audio. Please try again.');
    } finally {
      setUploadingAudio(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mentality-300"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-mentality-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/admin" className="flex items-center space-x-3">
              <img 
                src="/mentality-logo.jpg" 
                alt="Mentality Logo" 
                className="w-10 h-10 md:w-12 md:h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-mentality-300 to-mentality-600 bg-clip-text text-transparent">
                  Manage Sessions
                </h1>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
            </Link>
            <div className="flex items-center space-x-2 md:space-x-4">
              <Link to="/admin">
                <Button variant="ghost" size="sm">
                  <span className="hidden md:inline">Dashboard</span>
                  <span className="md:hidden">📊</span>
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={() => logout().then(() => navigate('/'))}
                className="border-mentality-200 hover:bg-mentality-50"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">The College Mentality Sessions</h2>
            <p className="text-gray-600">Manage available sessions for students</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-mentality-300 to-mentality-500 hover:from-mentality-400 hover:to-mentality-600 text-white" size="lg">
                + Add New Session
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSession ? 'Edit Session' : 'Add New Session'}</DialogTitle>
                <DialogDescription>
                  {editingSession ? 'Update the session details' : 'Create a new wellness session for students'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="the-college-mentality">The College Mentality</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="difficulty">Difficulty *</Label>
                    <Select value={formData.difficulty} onValueChange={(value) => setFormData({...formData, difficulty: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="duration">Duration (minutes) *</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="image_url">Image</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        id="image_url"
                        type="url"
                        placeholder="https://example.com/image.jpg"
                        value={formData.image_url}
                        onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                        className="flex-1"
                      />
                      <label htmlFor="image-file-upload">
                        <Button
                          type="button"
                          variant="outline"
                          className="border-mentality-300 text-mentality-600 hover:bg-mentality-50"
                          disabled={uploadingImage}
                          onClick={() => document.getElementById('image-file-upload').click()}
                        >
                          {uploadingImage ? 'Uploading...' : '📤 Upload'}
                        </Button>
                      </label>
                      <input
                        id="image-file-upload"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">Upload an image or enter URL directly</p>
                  </div>
                </div>
                <div>
                  <Label htmlFor="audio_url">Audio File *</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        id="audio_url"
                        type="url"
                        placeholder="https://sfo2.digitaloceanspaces.com/mentality-audio/session.m4a"
                        value={formData.audio_url}
                        onChange={(e) => setFormData({...formData, audio_url: e.target.value})}
                        className="flex-1"
                      />
                      <label htmlFor="audio-file-upload">
                        <Button
                          type="button"
                          variant="outline"
                          className="border-mentality-300 text-mentality-600 hover:bg-mentality-50"
                          disabled={uploadingAudio}
                          onClick={() => document.getElementById('audio-file-upload').click()}
                        >
                          {uploadingAudio ? 'Uploading...' : '📤 Upload'}
                        </Button>
                      </label>
                      <input
                        id="audio-file-upload"
                        type="file"
                        accept="audio/mpeg,audio/mp3,audio/m4a,audio/mp4,audio/wav"
                        onChange={handleAudioUpload}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">Upload audio (MP3, M4A, WAV) or enter URL directly</p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-mentality-300 to-mentality-500 hover:from-mentality-400 hover:to-mentality-600 text-white">
                    {editingSession ? 'Update Session' : 'Create Session'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Sessions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sessions.map((session) => (
            <Card key={session.id} className="overflow-hidden border-2 hover:border-mentality-300 transition-all">
              <div className="h-48 overflow-hidden">
                <img
                  src={session.image_url || 'https://via.placeholder.com/400x300'}
                  alt={session.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between mb-2">
                  <Badge className="bg-mentality-100 text-mentality-700">
                    {session.category}
                  </Badge>
                  <span className="text-sm text-gray-500">{session.duration} min</span>
                </div>
                <CardTitle className="text-lg">{session.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{session.description}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(session)}
                    className="flex-1"
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(session.id)}
                    className="flex-1"
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default ManageSessions;
